import * as introspectionQueryResultData from '@tableau/data-catalog-api-schema/dist/fragments.json';
import { ApolloCache } from 'apollo-cache';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import ApolloClient, { QueryBaseOptions } from 'apollo-client';
import { setContext } from 'apollo-link-context';
import { createHttpLink, FetchOptions } from 'apollo-link-http';
// Using the 'require' syntax because we need to use the 'main' (not 'module') form of unfetch, as
// only the 'main' form is interpreted as a function.
import unfetch = require('unfetch/dist/unfetch.js');
import { getCookieValue } from './utils/CookieHelpers';

const DEFAULT_LINK_OPTIONS: Readonly<FetchOptions> = {
  uri: '/api/exp/metadata/graphql',
  credentials: 'same-origin',
  fetch: unfetch,
};

const XSRF_COOKIE_NAME = 'XSRF-TOKEN';
const XSRF_PROTECTION_HEADER_NAME = 'X-XSRF-TOKEN';

export type DataCatalogApolloClient = ApolloClient<unknown>;

// Cache is disabled because of TFS 882415.
const useApolloCache = false;

export const queryOptions: QueryBaseOptions = {
  fetchPolicy: useApolloCache ? 'cache-first' : 'no-cache', // cache-first is the default
  errorPolicy: 'all', // 'none' is the default, but we want all errors passed through to our handlers
};

function getXSRFHeaderLink(xsrfToken?: string) {
  const XSRFHeaderLink = setContext((_, { headers }) => {
    const token = xsrfToken || getCookieValue(window.document.cookie, XSRF_COOKIE_NAME);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const newHeaders = { ...headers };

    if (token) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      newHeaders[XSRF_PROTECTION_HEADER_NAME] = token;
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      headers: newHeaders
    };
  });

  return XSRFHeaderLink;
}

/**
 * Create an apollo client to query the data catalog GraphQL API
 * @param linkOptions describes how to access the data catalog GraphQL API service
 */
export function apolloClientGenerator(linkOptions: FetchOptions = DEFAULT_LINK_OPTIONS, xsrfToken?: string): DataCatalogApolloClient {
  return new ApolloClient({
    // Cast required because the way apollo-link-context exports its link type is sad panda
    link: getXSRFHeaderLink(xsrfToken).concat(createHttpLink(linkOptions)),
    cache: createDataCatalogInMemoryCache(),
    defaultOptions: {
      query: queryOptions,
      watchQuery: queryOptions,
    },
  });
}

/**
 * Creates an in-memory cache for responses from the data catalog GraphQL API
 */
export function createDataCatalogInMemoryCache(): ApolloCache<unknown> {
  const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData });
  return new InMemoryCache({ fragmentMatcher });
}
